import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError, money, fmtDate, openBlob } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { METHOD_LABEL } from "@/lib/constants";

const Stat = ({ k, v, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
    <p className="mt-1 font-mono text-lg font-semibold" data-testid={testId}>{v}</p>
  </div>
);

const ReceiptButton = ({ entry }) => (
  <button className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline" data-testid={`receipt-btn-${entry.id}`}
    onClick={() => openBlob(`/ledger/${entry.id}/receipt.pdf`, `receipt-${entry.id.slice(-8).toUpperCase()}.pdf`).catch(() => toast.error("Could not load the receipt"))}>
    <Receipt className="h-3.5 w-3.5" /> Receipt
  </button>
);

export const EntriesTable = ({ entries, action, showRef = false }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full text-sm" data-testid="billing-entries-table">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="p-3">Date</th><th className="p-3">Amount</th><th className="p-3">Type</th><th className="p-3">Method</th>{showRef && <th className="p-3">Reference</th>}<th className="p-3">Note</th>{action && <th className="p-3" />}<th className="p-3">Receipt</th></tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className="border-t border-slate-100" data-testid={`ledger-row-${e.id}`}>
            <td className="whitespace-nowrap p-3">{fmtDate(e.created_at)}</td>
            <td className="whitespace-nowrap p-3 font-mono font-semibold">{["refund", "credit"].includes(e.type) ? "−" : ""}{money(e.amount)}</td>
            <td className="p-3"><StatusPill status={e.type} /></td>
            <td className="whitespace-nowrap p-3">{METHOD_LABEL[e.method] || e.method}</td>
            {showRef && <td className="whitespace-nowrap p-3 font-mono text-xs">{e.reference && !e.reference.startsWith("cs_") && !e.reference.startsWith("in_") ? e.reference : ""}</td>}
            <td className="max-w-[260px] truncate p-3 text-slate-500" title={e.note}>{e.note}{e.refunded_amount > 0 && <span className="ml-1 text-red-600">({money(e.refunded_amount)} refunded)</span>}</td>
            {action && <td className="p-3 text-right">{action(e)}</td>}
            <td className="p-3 text-right">{e.type === "payment" && <ReceiptButton entry={e} />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const BillingCard = ({ policyId, comped = false }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/policies/${policyId}/billing`).then((r) => setData(r.data)).catch(() => {});
  }, [policyId]);
  if (!data) return null;
  const b = data.billing;
  const pay = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/policies/${policyId}/pay-balance`, { origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };
  return (
    <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="billing-card">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold">Billing</h2>
          <StatusPill status={b.billing_status} testId="billing-status" />
        </div>
        {b.balance > 0 && (
          <Button onClick={pay} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white" data-testid="pay-balance-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />} Pay {money(b.balance)} now
          </Button>
        )}
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <Stat k="Plan" v={comped ? "No charge" : b.payment_plan === "full" ? "Paid in full" : b.autopay ? "Monthly auto-pay" : "Monthly"} testId="billing-plan" />
        <Stat k="Balance due" v={money(Math.max(b.balance, 0))} testId="billing-balance" />
        <Stat k="Total paid" v={money(b.paid)} testId="billing-paid" />
        <Stat k="Next payment" v={b.next_due_date ? fmtDate(b.next_due_date) : "—"} testId="billing-next-due" />
        <Stat k="Policy total" v={money(b.contract_total)} testId="billing-contract-total" />
      </div>
      <h3 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-slate-500">Payment history</h3>
      {data.entries.length ? <EntriesTable entries={data.entries} /> : <p className="text-sm text-slate-400">No payments yet.</p>}
    </section>
  );
};
